import React, {useEffect, useRef} from "react";
import {observer} from "mobx-react";
import {useServices} from "../../../../../hooks/use-services.hook";
import styled, {keyframes} from "styled-components";
import {InputBaseBox} from "../../../../../basic-components/input/input-base.box";
import {
    INuveiCardStrategyViewModel
} from "../../../../../services/booking/models/payment/methods/card/strategies/nuvei/nuvei-card-strategy.view-model.interface";
import {
    INuveiCardFieldViewModel
} from "../../../../../services/booking/models/payment/methods/card/strategies/nuvei/nuvei-card-field.view-model.interface";
import {SupportedCardNetworksSmallComponent} from "../supported-card-networks-small.component";



const CardContainerBox = styled.div<{$isDisabled: boolean}>`
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.spacing.flexGap20};
    width: 100%;
    ${props => props.$isDisabled ? 'opacity: 0.5;' : ''}
`


const CardFieldsBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${props => props.theme.spacing.flexGap20};

    ${props => props.theme.containerMediaQuery.smallScreen} {
        flex-direction: column;
    }
`

const CardFieldContainerBox = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`

const CardFieldInputBox = styled.div<{$isVisible: boolean}>`
    ${props => props.$isVisible ? '' : 'display: none;'}
    width: 100%;
    border: 1px solid ${props => props.theme.border.mainColor};
    border-radius: ${props => props.theme.border.defaultRadius};
    padding: ${props => props.theme.spacing.spacing20} ${props => props.theme.spacing.spacing16};
    font-size: 16pt;
    &:focus-within {
        border-color: ${props => props.theme.border.focusedColor};
    }
    .SfcField {
        width: 100%;
    }
`

const CardFieldLabelBox = styled.label`
    font-size: ${props => props.theme.fontSize.h3};
    font-weight: ${props => props.theme.fontWeight.semiBold};
`

const InputBox = styled(InputBaseBox)`
    width: 100%;
`

const shakeAnimation = keyframes`
    0% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
    20%, 40%, 60%, 80% { transform: translateX(5px); }
    100% { transform: translateX(0); }
`;

const ErrorMessageBox = styled.div`
    font-size: ${props => props.theme.fontSize.body1};
    color: ${props => props.theme.colors.danger};
    width: 100%;
    text-align: center;
    animation: ${shakeAnimation} 0.6s ease-in-out;
`;

const CardFieldErrorBox = styled.div`
    position: absolute;
    bottom: 0;
    font-size: ${props => props.theme.fontSize.body2};
    color: ${props => props.theme.colors.danger};
    transform: translateY(100%);
`


const CardHolderComponent: React.FC<{nuveiStrategy: INuveiCardStrategyViewModel}> = observer((props) => {
    const services = useServices();
    const inputRef = useRef<HTMLInputElement | null>(null);

    const cardHolderField = props.nuveiStrategy.cardHolderField;

    useEffect(() => {
        if(inputRef.current) {
            cardHolderField.attach(inputRef.current);
        }
    }, [cardHolderField]);

    const renderError = () => {
        if(!cardHolderField.errorMessage || props.nuveiStrategy.isDisabled) {
            return null;
        }

        return (
            <CardFieldErrorBox>
                {cardHolderField.errorMessage}
            </CardFieldErrorBox>
        )
    }

    return (
        <CardFieldContainerBox>
            <CardFieldLabelBox>
                {services.language.translate('Card holder')}
            </CardFieldLabelBox>
            <CardFieldInputBox $isVisible={true}>
                <InputBox ref={inputRef}  placeholder={props.nuveiStrategy.isDisabled ? "" : services.language.translate('Name on card')}
                              value={props.nuveiStrategy.isDisabled ? "" : cardHolderField.cardHolderName}
                              readOnly={props.nuveiStrategy.isDisabled}
                              onChange={e => cardHolderField.cardHolderName = e.target.value}/>
            </CardFieldInputBox>
            {renderError()}
        </CardFieldContainerBox>
    );
})



interface CardFieldComponentProps {
    field: INuveiCardFieldViewModel;
    label: string;
    isDisabled: boolean;
    className?: string;
}

const CardFieldComponent: React.FC<CardFieldComponentProps> = observer((props) => {
    const fieldInputContainerRef = useRef<HTMLDivElement | null>(null);
    const isMountedRef = useRef(false);

    useEffect(() => {
        const fieldElement = fieldInputContainerRef.current;
        if(fieldElement && !isMountedRef.current) {
            isMountedRef.current = true;
            props.field.attach(fieldElement);

        }

    }, [props.field]);

    const renderError = () => {
        if(!props.field.errorMessage || props.isDisabled) {
            return null;
        }

        return (
            <CardFieldErrorBox>
                {props.field.errorMessage}
            </CardFieldErrorBox>
        )
    }


    return (
        <CardFieldContainerBox className={props.className}>
            <CardFieldLabelBox>
                {props.label}
            </CardFieldLabelBox>
            <CardFieldInputBox ref={fieldInputContainerRef} $isVisible={!props.isDisabled}/>
            <CardFieldInputBox $isVisible={props.isDisabled}>
                <InputBox readOnly={true}/>
            </CardFieldInputBox>
            {renderError()}
        </CardFieldContainerBox>
    )
});

const CardNumberBox = styled(CardFieldComponent)`
    flex-basis: 55%;
    ${props => props.theme.containerMediaQuery.smallScreen} {
        flex-basis: unset;
    }
`

const CardExpirationBox = styled(CardFieldComponent)`
    flex-basis: 25%;
    ${props => props.theme.containerMediaQuery.smallScreen} {
        flex-basis: unset;
    }
`

const SecurityCodeBox = styled(CardFieldComponent)`
    flex-basis: 20%;
    ${props => props.theme.containerMediaQuery.smallScreen} {
        flex-basis: unset;
    }
`

export const NuveiCardPaymentMethodsTabContentComponent: React.FC<{nuveiStrategy: INuveiCardStrategyViewModel}> = observer((props) => {
    const services = useServices();


    const renderError = () => {
        if(!props.nuveiStrategy.errorMessage) {
            return null;
        }

        return (
            <ErrorMessageBox>
                {props.nuveiStrategy.errorMessage}
            </ErrorMessageBox>
        )
    }

    return (
        <CardContainerBox $isDisabled={props.nuveiStrategy.isDisabled}>
            <SupportedCardNetworksSmallComponent networks={props.nuveiStrategy.supportedCardNetworks}/>
            <CardHolderComponent nuveiStrategy={props.nuveiStrategy} />
            <CardFieldsBox>
                <CardNumberBox field={props.nuveiStrategy.cardNumberField}
                               label={services.language.translate('Card number')}
                               isDisabled={props.nuveiStrategy.isDisabled}/>

                <CardExpirationBox field={props.nuveiStrategy.expirationDateField}
                                    label={services.language.translate('Expiration date')}
                                    isDisabled={props.nuveiStrategy.isDisabled}/>
                <SecurityCodeBox field={props.nuveiStrategy.securityCodeField}
                                    label={services.language.translate('Security code')}
                                    isDisabled={props.nuveiStrategy.isDisabled}/>
            </CardFieldsBox>
            {renderError()}
        </CardContainerBox>
    );
})